import React, { useEffect, useCallback } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Typography,
  TextField,
  Grid,
  Box,
  Card,
  CardContent,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogContentText,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit, Delete, Add } from '@mui/icons-material';
import {
  CalculationFormProps,
  CalculationValues,
  CalculationResults,
  ConfigValues,
} from './types';
import { debounce } from './utils/autosave';
import { PersistentDB } from './utils/persistentDB';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import { canViewBalancePanel } from '@shared/utils/balancePermissions';
import {
  formatCostaRicanCurrency,
  convertHoursToUT,
} from './utils/currencyFormatter';

const validationSchema = Yup.object({
  // Parts
  netParts: Yup.number()
    .min(0, 'El neto debe ser mayor o igual a 0')
    .required('El neto de repuestos es requerido'),
  bodyworkHours: Yup.number()
    .min(0, 'Las horas deben ser mayor o igual a 0')
    .required('Las horas de carrocería son requeridas'),
  paintHours: Yup.number()
    .min(0, 'Las horas deben ser mayor o igual a 0')
    .required('Las horas de pintura son requeridas'),
  mechanicalHours: Yup.number()
    .min(0, 'Las horas deben ser mayor o igual a 0')
    .required('Las horas de mecánica son requeridas'),
  hiddenDamagePartsNoVAT: Yup.number()
    .min(0, 'El daño oculto debe ser mayor o igual a 0')
    .required('El daño oculto RE es requerido'),
  hiddenDamageLaborNoVAT: Yup.number()
    .min(0, 'El daño oculto debe ser mayor o igual a 0')
    .required('El daño oculto MO es requerido'),
  additionalLabor: Yup.number()
    .min(0, 'Los adicionales deben ser mayor o igual a 0')
    .required('Los adicionales MO son requeridos'),
  additionalParts: Yup.number()
    .min(0, 'Los adicionales deben ser mayor o igual a 0')
    .required('Los adicionales RE son requeridos'),
  subletAmount: Yup.number()
    .min(0, 'El monto sublet debe ser mayor o igual a 0')
    .required('El monto sublet es requerido'),
});

const CalculationForm: React.FC<CalculationFormProps> = ({
  poNumber,
  claimUId,
  configValues,
  partsVariables,
  laborVariables,
  initialCalculationValues,
  siseSiniestro,
  onRefresh,
  estimations = [],
  isJsonLoaded,
  editMode,
  onCancelEdit,
}) => {
  // Generar ID alfanumérico aleatorio
  const generateRandomId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const [hasUserInteracted, setHasUserInteracted] = React.useState<boolean>(
    false,
  );
  const [previousFormValues, setPreviousFormValues] = React.useState<any>(null);

  // Estado para estimationId - cargar desde localStorage o generar uno nuevo
  const [estimationId, setEstimationId] = React.useState<string>(() => {
    try {
      const savedEstimationId = localStorage.getItem('current_estimation_id');
      if (savedEstimationId) {
        console.log(
          '📝 EstimationId cargado desde localStorage:',
          savedEstimationId,
        );
        return savedEstimationId;
      }
    } catch (error) {
      console.warn(
        '⚠️ Error al cargar estimationId desde localStorage:',
        error,
      );
    }
    // Generar nuevo ID alfanumérico si no existe
    const newId = generateRandomId();
    console.log('🆕 Nuevo estimationId generado:', newId);
    return newId;
  });

  // Estado para el panel Balance
  const [balanceData, setBalanceData] = React.useState<any[]>([]);
  //const [nextId, setNextId] = React.useState<number>(1);

  // Log del estado completo del balance cuando cambie
  React.useEffect(() => {
    console.group('📊 ESTADO COMPLETO DEL BALANCE');
    console.log('🗂️ Balance data actual:', balanceData);
    console.log(
      '📋 JSON completo del balance:',
      JSON.stringify(balanceData, null, 2),
    );
    console.log('📈 Total de registros:', balanceData.length);
    console.log(
      '💰 Suma total de montos:',
      balanceData.reduce((sum, item) => sum + (item.amount || 0), 0),
    );
    console.log(
      '💵 Suma total de NC Repuesto:',
      balanceData.reduce((sum, item) => sum + (item.sparePartsNC || 0), 0),
    );
    console.groupEnd();
  }, [balanceData]);

  // Estado para el modal
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const [editingItem, setEditingItem] = React.useState<any>(null);
  const [
    openConfirmSaveEstimation,
    setOpenConfirmSaveEstimation,
  ] = React.useState<boolean>(false);

  const loadBalanceData = useCallback(async (): Promise<void> => {
    if (!canViewBalancePanel()) {
      return;
    }
    const balanceDataResponse = await new ClaimsTrackerClient().getCustomBalancesByEstimation(
      claimUId,
      localStorage.getItem('current_estimation_id') ?? estimationId,
    );
    if (balanceDataResponse.status === 200) {
      console.info('balanceDataResponse', balanceDataResponse);
      setBalanceData(balanceDataResponse.data?.result || []);
    }
  }, [claimUId, estimationId]);

  // Función helper para persistir balanceData
  // @ts-expect-error - Función mantenida para uso futuro
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _persistBalanceData = (newBalanceData: any[]) => {
    try {
      const activeEstimation = PersistentDB.getActiveEstimation();
      if (activeEstimation) {
        // Actualizar la estimación activa con el nuevo balanceData
        PersistentDB.updateEstimation(activeEstimation._id!, {
          ...activeEstimation,
          balanceData: newBalanceData,
        });
        console.log('💾 Balance data persisted successfully:', newBalanceData);
      } else {
        console.warn('⚠️ No active estimation to persist balance data');
      }
    } catch (error) {
      console.error('❌ Error persisting balance data:', error);
    }
  };

  const balanceTotalAmount = () => {
    // Separar montos positivos y negativos para manejar correctamente el cálculo
    const positiveAmounts = balanceData
      .filter((item) => item.amount > 0 && !item.isDeleted)
      .reduce((sum, item) => sum + item.amount, 0);

    const negativeAmounts = balanceData
      .filter((item) => item.amount < 0 && !item.isDeleted)
      .reduce((sum, item) => sum + item.amount, 0);

    const sparePartsDiscount = balanceData
      .filter((item) => !item.isDeleted)
      .reduce((sum, item) => sum + (item.sparePartsNC || 0), 0);

    // Los positivos se restan, los negativos se suman (porque ya son negativos)
    // ((Montos positivos) + (Montos negativos)) - (Descuento de Repuesto)
    const totalAmounts = positiveAmounts + negativeAmounts;

    return totalAmounts - sparePartsDiscount;
  };

  // Calcular saldo balance con la nueva fórmula
  const calculateBalanceAmount = (totalLossAmount: number, vat: number) => {
    console.info('💰 Cálculo de Monto Total de Pérdida:', totalLossAmount);
    console.info('💰 Cálculo de IVA:', (vat * totalLossAmount) / 100);
    console.info(
      '💰 Cálculo de Monto Total de Pérdida sin IVA:',
      totalLossAmount / (1 + configValues.vat / 100) - configValues.deductible,
    );

    const totalLossAmountWithoutIVA =
      totalLossAmount / (1 + configValues.vat / 100);
    const finalTotalLossAmount =
      (totalLossAmountWithoutIVA || 0) - configValues.deductible;

    // Fórmula corregida:
    // Monto Total de pérdida - Total Balance

    const result = finalTotalLossAmount - balanceTotalAmount(); //+ depreciation + deductible;

    console.info('Total loss amount:', finalTotalLossAmount);
    console.info('Balance total amount:', balanceTotalAmount());
    console.info('Result:', result);

    return result;
  };

  // Función para validar si el estimationId existe en las estimaciones históricas
  const isValidEstimationId = (): boolean => {
    const currentEstimationId =
      localStorage.getItem('current_estimation_id') ?? estimationId;

    if (!currentEstimationId) {
      return false;
    }

    // Verificar si el estimationId existe en la lista de estimaciones históricas
    const exists = estimations.some(
      (est) =>
        est.estimationId === currentEstimationId ||
        est._id === currentEstimationId,
    );

    return exists;
  };

  // Funciones para manejar el balance
  const handleAddBalance = () => {
    if (!canViewBalancePanel()) {
      return;
    }
    if (!isValidEstimationId()) {
      alert(
        'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a agregar una factura.',
      );
      return;
    }
    setEditingItem(null);
    setOpenModal(true);
  };

  const handleEditBalance = (id: string | number) => {
    if (!canViewBalancePanel()) {
      return;
    }
    if (!isValidEstimationId()) {
      alert(
        'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a actualizar una factura.',
      );
      return;
    }

    const item = balanceData.find(
      (item) => item._id === id || item.balanceId === id || item.id === id,
    );
    if (item) {
      // Validar que el estimationId del balance exista en las estimaciones históricas
      const balanceEstimationId = item.estimationId;
      const balanceEstimationExists = estimations.some(
        (est) =>
          est.estimationId === balanceEstimationId ||
          est._id === balanceEstimationId,
      );

      if (!balanceEstimationExists) {
        alert(
          'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a actualizar una factura.',
        );
        return;
      }

      console.group('✏️ ABRIENDO MODAL DE EDICIÓN');
      console.log('🆔 ID del item a editar:', id);
      console.log('📊 Item encontrado:', item);
      console.log('📋 JSON del item a editar:', JSON.stringify(item, null, 2));
      console.groupEnd();

      setEditingItem(item);
      setOpenModal(true);
    }
  };

  const handleDeleteBalance = (id: string | number) => {
    if (!canViewBalancePanel()) {
      return;
    }
    if (!isValidEstimationId()) {
      alert(
        'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a eliminar una factura.',
      );
      return;
    }

    const itemToDelete = balanceData.find(
      (item) => item._id === id || item.balanceId === id || item.id === id,
    );

    if (!itemToDelete) {
      return;
    }

    // Validar que el estimationId del balance exista en las estimaciones históricas
    const balanceEstimationId = itemToDelete.estimationId;
    const balanceEstimationExists = estimations.some(
      (est) =>
        est.estimationId === balanceEstimationId ||
        est._id === balanceEstimationId,
    );

    if (!balanceEstimationExists) {
      alert(
        'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a eliminar una factura.',
      );
      return;
    }

    console.group('🗑️ ELIMINANDO REGISTRO DE BALANCE');
    console.log('🆔 ID del item a eliminar:', id);
    console.log('📊 Item a eliminar:', itemToDelete);
    console.log(
      '📋 JSON del item eliminado:',
      JSON.stringify(itemToDelete, null, 2),
    );
    console.log('🗂️ Balance data antes de eliminar:', balanceData);

    (async () => {
      const balanceDataResponse = await new ClaimsTrackerClient().deleteBalanceAmount(
        itemToDelete._id,
      );
      if (balanceDataResponse.status === 200) {
        await loadBalanceData();
      }
    })();
    console.groupEnd();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingItem(null);
  };

  const handleSaveBalance = (formData: any) => {
    if (!canViewBalancePanel()) {
      return;
    }
    // Validar que el estimationId exista antes de guardar
    if (!isValidEstimationId()) {
      alert(
        'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a agregar una factura.',
      );
      return;
    }

    // Calcular NC Repuesto basado en el monto y el porcentaje de descuento (solo para FREXP)
    const amount = formData.amount || 0;
    const discountPercentage = configValues.discount || 0;
    const sparePartsNC =
      formData.documentType === 'FREXP'
        ? amount * (discountPercentage / 100)
        : 0;

    // Para CDEDXP, el monto debe ser siempre negativo (resta del balance)
    // Para otros tipos, mantener el monto tal como se ingresa (puede ser positivo o negativo)
    const finalAmount =
      formData.documentType === 'CDEDXP' ? -Math.abs(amount) : amount;

    // Log para mostrar la conversión de CDEDXP
    if (formData.documentType === 'CDEDXP') {
      console.log('💰 CDEDXP - Conversión de monto a negativo:', {
        montoOriginal: amount,
        montoFinal: finalAmount,
        tipoDocumento: formData.documentType,
      });
    }

    // Model data for balance
    const itemData = {
      claimUId: claimUId,
      estimationId:
        localStorage.getItem('current_estimation_id') ?? estimationId,
      balanceId: formData.balanceId,
      timestamp: new Date().toISOString(),
      isDeleted: false,
      deletedAt: null,
      documentType: formData.documentType,
      documentNumber: formData.documentNumber,
      date: formData.date,
      recipient: formData.recipient,
      amount: finalAmount,
      sparePartsNC: Math.round(sparePartsNC * 100) / 100, // Redondear a 2 decimales
      observation: formData.observation,
    };

    if (editingItem) {
      // Validar que el estimationId del balance exista en las estimaciones históricas
      const balanceEstimationId = editingItem.estimationId;
      const balanceEstimationExists = estimations.some(
        (est) =>
          est.estimationId === balanceEstimationId ||
          est._id === balanceEstimationId,
      );

      if (!balanceEstimationExists) {
        alert(
          'Se debe cargar desde el histórico de estimaciones o crear una estimación antes de proceder a actualizar una factura.',
        );
        return;
      }

      // Editar item existente
      const updatedItem = { ...editingItem, ...itemData };
      const dataToSave = {
        claimUId: claimUId,
        CustomEstimationId:
          localStorage.getItem('current_estimation_id') ?? estimationId,
        jsonBalanceData: JSON.stringify(updatedItem),
        balanceId: editingItem.balanceId,
      };

      console.group('🔄 EDITANDO REGISTRO DE BALANCE');
      console.log('📝 Datos del formulario:', formData);
      console.log('💰 Cálculos aplicados:', {
        amount: amount,
        discountPercentage: discountPercentage,
        sparePartsNCCalculated: sparePartsNC,
        sparePartsNCRounded: Math.round(sparePartsNC * 100) / 100,
      });
      console.log('🔧 Configuración actual:', {
        discount: configValues.discount,
        vat: configValues.vat,
      });
      console.log('📊 Item actualizado completo:', updatedItem);
      console.log(
        '📋 JSON final del registro:',
        JSON.stringify(updatedItem, null, 2),
      );
      console.groupEnd();

      /*const newBalanceData = balanceData.map((item) => {
        const itemId = item._id || item.balanceId || item.id;
        const editingItemId =
          editingItem._id || editingItem.balanceId || editingItem.id;
        return itemId === editingItemId ? updatedItem : item;
      });
      setBalanceData(newBalanceData);

      // Persistir los cambios
      persistBalanceData(newBalanceData);*/
      (async () => {
        const balanceDataResponse = await new ClaimsTrackerClient().saveBalanceAmount(
          dataToSave,
        );
        if (balanceDataResponse.status === 200) {
          await loadBalanceData();
        }
      })();
    } else {
      // Agregar nuevo item
      const dataItem = {
        ...itemData,
        claimNumber: siseSiniestro || 'not-defined-claim-number',
      };

      const dataToSave = {
        claimUId: claimUId,
        CustomEstimationId:
          localStorage.getItem('current_estimation_id') ?? estimationId,
        jsonBalanceData: JSON.stringify(dataItem),
        balanceId: formData.balanceId,
      };

      console.group('➕ AGREGANDO NUEVO REGISTRO DE BALANCE');
      console.log('📝 Datos del formulario:', formData);
      console.log('💰 Cálculos aplicados:', {
        amount: amount,
        discountPercentage: discountPercentage,
        sparePartsNCCalculated: sparePartsNC,
        sparePartsNCRounded: Math.round(sparePartsNC * 100) / 100,
      });
      console.log('🔧 Configuración actual:', {
        discount: configValues.discount,
        vat: configValues.vat,
      });
      console.log(
        '🎲 ID generado:',
        siseSiniestro
          ? `SISE: ${siseSiniestro}`
          : `Aleatorio: ${generateRandomId()}`,
      );
      console.log('📊 Nuevo item completo:', dataToSave);
      console.log(
        '📋 JSON final del registro:',
        JSON.stringify(dataToSave, null, 2),
      );
      //console.log('🗂️ Balance data actualizada:', [...balanceData, newItem]);
      console.groupEnd();

      //const newBalanceData = [...balanceData, newItem];
      //setBalanceData(newBalanceData);
      //setNextId(nextId + 1);

      // Persistir los cambios
      //persistBalanceData(newBalanceData);
      (async () => {
        const balanceDataResponse = await new ClaimsTrackerClient().saveBalanceAmount(
          dataToSave,
        );
        if (balanceDataResponse.status === 200) {
          await loadBalanceData();
        }
      })();
    }
    handleCloseModal();
  };

  // Definir columnas del DataGrid con auto-sizing y ajuste manual
  const balanceColumns: GridColDef[] = [
    {
      field: 'claimNumber',
      headerName: '# Siniestro',
      minWidth: 100,
      maxWidth: 150,
      flex: 0.8,
      editable: true,
    },
    {
      field: 'documentType',
      headerName: 'Tipo de Documento',
      minWidth: 180,
      maxWidth: 250,
      flex: 1.2,
      editable: true,
      valueFormatter: (params) => {
        const tipoMap: { [key: string]: string } = {
          FMOXP: 'Factura MO (x Pagar)',
          FREXP: 'Factura RE (x Pagar)',
          CDEDXP: 'Cobro Deducible (x Pagar)',
          ADJGAS: 'Ajuste (Gasto)',
        };
        return tipoMap[params.value] || params.value;
      },
    },
    {
      field: 'documentNumber',
      headerName: '# de Documento',
      minWidth: 120,
      maxWidth: 200,
      flex: 1,
      editable: true,
    },
    {
      field: 'date',
      headerName: 'Fecha',
      minWidth: 100,
      maxWidth: 130,
      flex: 0.7,
      editable: true,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Convertir string a Date para mostrar en formato dd/MM/yyyy
        const date = new Date(params.value);
        return date.toLocaleDateString('es-CR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
      valueGetter: (params) => {
        if (!params.value) return null;
        // Convertir string a Date para el DataGrid
        return new Date(params.value);
      },
    },
    {
      field: 'recipient',
      headerName: 'Receptor',
      minWidth: 150,
      maxWidth: 300,
      flex: 1.5,
      editable: true,
    },
    {
      field: 'amount',
      headerName: 'Monto',
      minWidth: 120,
      maxWidth: 180,
      flex: 1,
      type: 'number',
      editable: true,
      renderCell: (params) => formatCostaRicanCurrency(params.value || 0),
    },
    {
      field: 'sparePartsNC',
      headerName: 'NC Repuesto',
      minWidth: 120,
      maxWidth: 180,
      flex: 1,
      editable: true,
      renderCell: (params) => formatCostaRicanCurrency(params.value || 0),
    },
    {
      field: 'observation',
      headerName: 'Observación',
      minWidth: 200,
      maxWidth: 400,
      flex: 2,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      minWidth: 100,
      maxWidth: 120,
      flex: 0.6,
      getActions: (params) => {
        const row = params.row;
        // Validar que el estimationId del balance exista en las estimaciones históricas
        const balanceEstimationId = row.estimationId;
        const balanceEstimationExists = estimations.some(
          (est) =>
            est.estimationId === balanceEstimationId ||
            est._id === balanceEstimationId,
        );
        const isEstimationValid =
          isValidEstimationId() && balanceEstimationExists;

        return [
          <GridActionsCellItem
            icon={<Edit />}
            label="Editar"
            onClick={() => handleEditBalance(params.id)}
            disabled={!isEstimationValid}
          />,
          <GridActionsCellItem
            icon={<Delete />}
            label="Borrar"
            onClick={() => handleDeleteBalance(params.id)}
            disabled={!isEstimationValid}
          />,
        ];
      },
    },
  ];

  // Helper function to handle field blur with autosave
  const handleFieldBlurWithAutosave = (field: any, values: any) => (e: any) => {
    setHasUserInteracted(true);
    field.onBlur(e);
    handleFieldBlur(values, false);
  };

  // Handler submit new estimation
  const handleSubmitSaveNewEstimation = (values: any) => {
    const newEstimationId = generateRandomId();
    setEstimationId(newEstimationId);
    localStorage.setItem('current_estimation_id', newEstimationId);
    handleSubmitSave(values);
  };

  // Handler submit estimation
  const handleSubmitSave = (values: any) => {
    setHasUserInteracted(true);
    handleFieldBlur(values, true);
  };

  // Helper function to handle field blur with autosave
  const handleFieldBlur = (values: any, isSubmit: boolean = false) => {
    console.info('handleFieldBlur', values);
    setHasUserInteracted(true);

    // Comparar con valores anteriores
    if (
      previousFormValues &&
      JSON.stringify(values) === JSON.stringify(previousFormValues) &&
      !isSubmit
    ) {
      console.log(
        '🚫 AUTOSAVE - Sin cambios detectados en cálculos, omitiendo autosave',
      );
      return;
    }

    const calculations = calculateValues(values);
    debouncedAutosave(values, calculations, isSubmit);

    // Guardar valores actuales para próxima comparación
    setPreviousFormValues(values);
    setOpenConfirmSaveEstimation(false);
  };

  // Autosave debounce for calculations
  const debouncedAutosave = debounce(
    (
      formData: CalculationValues,
      calculations: CalculationResults,
      isSubmit: boolean = false,
    ) => {
      if (!hasUserInteracted) return; // No autosave en carga inicial

      // Comparar con valores anteriores
      if (
        previousFormValues &&
        JSON.stringify(formData) === JSON.stringify(previousFormValues) &&
        !isSubmit
      ) {
        console.log('🚫 AUTOSAVE - Sin cambios detectados, omitiendo autosave');
        return;
      }

      // Crear estructura JSON completa según el formato requerido
      // Usar estimationId del estado (puede venir del histórico o ser generado)
      const estimationIdData =
        localStorage.getItem('current_estimation_id') ?? estimationId;
      const completeJsonData = {
        estimationId: estimationIdData,
        poNumber: poNumber,
        claimUId: claimUId,
        timestamp: new Date().toISOString(),
        isDeleted: false,
        deletedAt: null,
        configValues: {
          ...configValues,
          isStructuralLoss: configValues.isStructuralLoss || false,
          isFinancialLoss: configValues.isFinancialLoss || false,
        },
        inputValues: {
          ...formData,
          bodyworkUT: convertHoursToUT(formData.bodyworkHours || 0),
          paintUT: convertHoursToUT(formData.paintHours || 0),
          mechanicalUT: convertHoursToUT(formData.mechanicalHours || 0),
        },
        calculations: calculations,
        conditionalVariables: {
          partsVariables: partsVariables || {
            workshop: 'Purdy Auto',
            company: 'Purdy Motor',
            brand: 'Toyota',
          },
          laborVariables: laborVariables || {
            workshop: 'Purdy Auto',
            type: 'Interno',
          },
        },
        summary: {
          totalPartsLoss: calculations.totalPartsLoss,
          totalLaborLoss: calculations.totalLaborLoss,
          totalLossAmount: calculations.totalLossAmount,
          finalIndemnityAmount: calculations.finalIndemnityAmount,
        },
        breakdown: {
          parts: {
            netParts: formData.netParts,
            discount: configValues.discount,
            vat: configValues.vat,
            netTotalParts: calculations.netTotalParts,
            preliminaryPartsLoss: calculations.preliminaryPartsLoss,
            hiddenDamagePartsWithVAT: calculations.hiddenDamagePartsWithVAT,
            totalPartsLoss: calculations.totalPartsLoss,
            conditionalDiscount: partsVariables
              ? `${partsVariables.workshop} + ${partsVariables.company} = ${configValues.discount}%`
              : 'No aplicado',
          },
          labor: {
            bodyworkHours: formData.bodyworkHours,
            paintHours: formData.paintHours,
            mechanicalHours: formData.mechanicalHours,
            bodyworkRate: configValues.bodyworkRate,
            paintRate: configValues.paintRate,
            mechanicalRate: configValues.mechanicalRate,
            preliminaryLaborNoVAT: calculations.preliminaryLaborNoVAT,
            preliminaryLabor: calculations.preliminaryLabor,
            hiddenDamageLaborWithVAT: calculations.hiddenDamageLaborWithVAT,
            totalLaborLoss: calculations.totalLaborLoss,
            conditionalLaborCost: laborVariables
              ? `${laborVariables.workshop} + ${laborVariables.type} = ₡${configValues.bodyworkRate}`
              : 'No aplicado',
          },
          adjustments: {
            underinsurance: configValues.underinsurance,
            depreciation: configValues.depreciation,
            deductible: configValues.deductible,
          },
        },
      };

      // Debug: Mostrar valores que se van a guardar
      console.log('💾 DEBUG - Values to save in API:', {
        totalLossAmount: completeJsonData.calculations.totalLossAmount,
        finalIndemnityAmount:
          completeJsonData.calculations.finalIndemnityAmount,
        calculations: completeJsonData.calculations,
      });

      // Save or update estimation in API
      try {
        if (isSubmit) {
          (async () => {
            if (editMode?.active && editMode.estimationId) {
              // EDIT MODE: Update existing estimation with editHistory
              const editHistoryEntry = {
                timestamp: new Date().toISOString(),
                userName:
                  localStorage.getItem('userName') ||
                  localStorage.getItem('userId') ||
                  'unknown',
                fieldsModified: [] as string[],
                previousValues: {} as Record<string, number>,
                newValues: {} as Record<string, number>,
              };

              // Detect which fields changed
              if (editMode.originalValues) {
                if (
                  formData.hiddenDamagePartsNoVAT !==
                  editMode.originalValues.hiddenDamagePartsNoVAT
                ) {
                  editHistoryEntry.fieldsModified.push(
                    'hiddenDamagePartsNoVAT',
                  );
                  editHistoryEntry.previousValues.hiddenDamagePartsNoVAT =
                    editMode.originalValues.hiddenDamagePartsNoVAT;
                  editHistoryEntry.newValues.hiddenDamagePartsNoVAT =
                    formData.hiddenDamagePartsNoVAT;
                }
                if (
                  formData.hiddenDamageLaborNoVAT !==
                  editMode.originalValues.hiddenDamageLaborNoVAT
                ) {
                  editHistoryEntry.fieldsModified.push(
                    'hiddenDamageLaborNoVAT',
                  );
                  editHistoryEntry.previousValues.hiddenDamageLaborNoVAT =
                    editMode.originalValues.hiddenDamageLaborNoVAT;
                  editHistoryEntry.newValues.hiddenDamageLaborNoVAT =
                    formData.hiddenDamageLaborNoVAT;
                }
              }

              // Don't save if no changes were made
              if (editHistoryEntry.fieldsModified.length === 0) {
                console.log(
                  '⚠️ No se detectaron cambios en los campos de daño oculto',
                );
                alert('No se detectaron cambios en los campos de daño oculto.');
                return;
              }

              // Add editHistory to the JSON
              const existingEditHistory =
                (completeJsonData as any).editHistory || [];
              const updatedJsonData = {
                ...completeJsonData,
                editHistory: [...existingEditHistory, editHistoryEntry],
              };

              try {
                await new ClaimsTrackerClient().updateCustomEstimation(
                  editMode.estimationId,
                  JSON.stringify(updatedJsonData),
                );

                // Refresh, reset interaction state, and exit edit mode
                setHasUserInteracted(false);
                setPreviousFormValues(null);
                if (onRefresh) onRefresh();
                if (onCancelEdit) onCancelEdit();
              } catch (error) {
                console.error('❌ Error al actualizar estimación:', error);
                alert(
                  'Error al actualizar la estimación. Por favor, inténtelo de nuevo.',
                );
              }
            } else {
              // NORMAL MODE: existing SaveEstimatedAmount logic
              await new ClaimsTrackerClient().SaveEstimatedAmount(
                {
                  partsCost: completeJsonData.calculations.totalPartsLoss.toString(),
                  porcDesc: completeJsonData.configValues.discount.toString(),
                  discountAmount: '0',
                  subtotalParts: completeJsonData.calculations.totalPartsLoss.toString(),
                  partsCostWithoutDiscount: '0',
                  partsCostTotal: completeJsonData.calculations.totalPartsLoss.toString(),
                  expenseAmount: completeJsonData.calculations.totalLaborLoss.toString(),
                  materialsCost: '0',
                  laborCost: completeJsonData.calculations.totalLaborLoss.toString(),
                  ivuLaborAmount: '0',
                  ivuPartsAmount: '0',
                  estimatedTotalAmount: completeJsonData.calculations.finalIndemnityAmount.toString(),
                  porcIVA: completeJsonData.configValues.vat.toString(),
                  ivaAmount: '0',
                  previousDamageCost: '0',
                  jsonCustomEstimateData: JSON.stringify(completeJsonData),
                  customEstimationId: estimationIdData,
                },
                claimUId,
                '',
              );

              // Refresh the estimations
              if (onRefresh) {
                onRefresh();

                // Get balance data from API
                await loadBalanceData();
              }
            }
          })();
        }
        //console.log('🔄 AUTOSAVE - Cálculos actualizados:', id);
        console.log(
          '📊 JSON completo generado:',
          JSON.stringify(completeJsonData, null, 2),
        );
        console.log('✅ Cálculos guardados automáticamente');
        console.log('---');

        // Guardar valores actuales para próxima comparación
        setPreviousFormValues(formData);
      } catch (error) {
        console.error('❌ Error en autosave:', error);
      }
    },
    2000,
  );

  const calculateValues = useCallback(
    (values: CalculationValues): CalculationResults => {
      const {
        netParts = 0,
        bodyworkHours = 0,
        paintHours = 0,
        mechanicalHours = 0,
        hiddenDamagePartsNoVAT = 0,
        hiddenDamageLaborNoVAT = 0,
        additionalLabor = 0,
        additionalParts = 0,
        subletAmount = 0,
      } = values;

      const {
        discount = 0,
        vat = 0,
        bodyworkRate = 0,
        paintRate = 0,
        mechanicalRate = 0,
        underinsurance = 0,
        depreciation = 0,
        deductible = 0,
      } = configValues;

      // CÁLCULOS DE REPUESTOS según fórmulas de la tabla

      // 1. Monto preliminar de pérdida (Repuestos): Neto * (1 - Descuento/100) = Neto Total; Neto Total * (1 + IVA/100) = Total
      const netTotalParts = (netParts || 0) * (1 - (discount || 0) / 100);
      const preliminaryPartsLoss = netTotalParts * (1 + (vat || 0) / 100);

      // 2. Monto total de pérdida (Repuestos): Monto preliminar + Daño Oculto RE
      const hiddenDamagePartsWithVAT =
        (hiddenDamagePartsNoVAT || 0) * (1 + (vat || 0) / 100);
      const totalPartsLoss = preliminaryPartsLoss + hiddenDamagePartsWithVAT;

      // CÁLCULOS DE MANO DE OBRA según fórmulas de la tabla

      // 3. Monto preliminar de pérdida (MO): Horas * Tarifa (por tipo) * (1 + IVA/100)
      const preliminaryLaborNoVAT =
        (bodyworkHours || 0) * (bodyworkRate || 0) +
        (paintHours || 0) * (paintRate || 0) +
        (mechanicalHours || 0) * (mechanicalRate || 0);
      const preliminaryLabor = preliminaryLaborNoVAT * (1 + (vat || 0) / 100);

      // 4. Monto total de pérdida (MO): Monto preliminar + Daño Oculto MO
      const hiddenDamageLaborWithVAT =
        (hiddenDamageLaborNoVAT || 0) * (1 + (vat || 0) / 100);
      const totalLaborLoss = preliminaryLabor + hiddenDamageLaborWithVAT;

      // CÁLCULOS TOTALES según fórmulas de la tabla

      // 5. Monto total de pérdida: Total Repuestos + Total MO + Adicionales (MO + RE) + Sublet
      const totalLossAmount =
        totalPartsLoss +
        totalLaborLoss +
        (additionalLabor || 0) +
        (additionalParts || 0) +
        (subletAmount || 0);

      // 6. Monto total de la reparación: Equivalente a monto total de pérdida
      const totalRepairAmount = totalLossAmount;

      // 7. Monto a Indemnizar Inicial: Monto total de la reparación - (Infraseguro + Depreciaciones)
      const initialIndemnityAmount =
        totalRepairAmount - ((underinsurance || 0) + (depreciation || 0));

      // 8. TOTAL MONTO INDEMNIZAR: Monto a Indemnizar Inicial tras ajustes finales (deducible)
      const finalIndemnityAmount = Math.max(
        0,
        initialIndemnityAmount - (deductible || 0),
      );

      // Asegurar que todos los valores sean números válidos
      const safeNumber = (value: number): number => {
        return isNaN(value) || !isFinite(value) ? 0 : value;
      };

      return {
        // Parts calculations
        netTotalParts: safeNumber(netTotalParts),
        preliminaryPartsLoss: safeNumber(preliminaryPartsLoss),
        hiddenDamagePartsWithVAT: safeNumber(hiddenDamagePartsWithVAT),
        totalPartsLoss: safeNumber(totalPartsLoss),

        // Labor calculations
        preliminaryLaborNoVAT: safeNumber(preliminaryLaborNoVAT),
        preliminaryLabor: safeNumber(preliminaryLabor),
        hiddenDamageLaborWithVAT: safeNumber(hiddenDamageLaborWithVAT),
        totalLaborLoss: safeNumber(totalLaborLoss),

        // Final calculations
        totalLossAmount: safeNumber(totalLossAmount),
        totalRepairAmount: safeNumber(totalRepairAmount),
        initialIndemnityAmount: safeNumber(initialIndemnityAmount),
        finalIndemnityAmount: safeNumber(finalIndemnityAmount),
      };
    },
    [configValues],
  );

  // Autosave en tiempo real cuando hay valores válidos - solo si el usuario ha interactuado
  useEffect(() => {
    if (initialCalculationValues && hasUserInteracted) {
      const calculations = calculateValues(initialCalculationValues);
      debouncedAutosave(initialCalculationValues, calculations);
    }
  }, [
    initialCalculationValues,
    configValues,
    partsVariables,
    laborVariables,
    hasUserInteracted,
    calculateValues,
    debouncedAutosave,
  ]);

  // Cargar balanceData y estimationId desde localStorage cuando se carga una estimación
  React.useEffect(() => {
    try {
      // Cargar estimationId si existe en localStorage
      const savedEstimationId = localStorage.getItem('current_estimation_id');
      if (savedEstimationId) {
        setEstimationId(savedEstimationId);
        console.log(
          '📝 EstimationId actualizado desde localStorage:',
          savedEstimationId,
        );
      }
    } catch (error) {
      console.error('❌ Error to load balance data:', error);
    }
  }, [claimUId, estimationId, loadBalanceData]);

  useEffect(() => {
    if (isJsonLoaded) {
      // Get balance data from API
      (async () => {
        await loadBalanceData();
      })();
    }
  }, [isJsonLoaded, loadBalanceData]);

  const initialValues: CalculationValues = initialCalculationValues || {
    netParts: 0,
    bodyworkHours: 0,
    paintHours: 0,
    mechanicalHours: 0,
    hiddenDamagePartsNoVAT: 0,
    hiddenDamageLaborNoVAT: 0,
    additionalLabor: 0,
    additionalParts: 0,
    subletAmount: 0,
    bodyworkUT: 0,
    paintUT: 0,
    mechanicalUT: 0,
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmitSave}
      >
        {({ values, errors, touched }) => {
          const calculated = calculateValues(values);

          return (
            <Form>
              {editMode?.active && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Modo Edición — Solo campos de Daño Oculto habilitados
                </Alert>
              )}
              <Grid container spacing={3}>
                {/* Repuestos */}
                <Grid item xs={12} lg={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Repuestos
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field name="netParts">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Neto Repuestos (₡)"
                                type="number"
                                fullWidth
                                disabled={editMode?.active}
                                error={
                                  touched.netParts && Boolean(errors.netParts)
                                }
                                helperText={touched.netParts && errors.netParts}
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={6}>
                          <Field name="hiddenDamagePartsNoVAT">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Daño Oculto RE Sin IVA (₡)"
                                type="number"
                                fullWidth
                                error={
                                  touched.hiddenDamagePartsNoVAT &&
                                  Boolean(errors.hiddenDamagePartsNoVAT)
                                }
                                helperText={
                                  touched.hiddenDamagePartsNoVAT &&
                                  errors.hiddenDamagePartsNoVAT
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>

                      {/* Resultados Repuestos */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Cálculos de Repuestos:
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Chip
                              label={`Neto Total: ${formatCostaRicanCurrency(
                                calculated.netTotalParts || 0,
                              )}`}
                              color="info"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={`Preliminar con IVA: ${formatCostaRicanCurrency(
                                calculated.preliminaryPartsLoss || 0,
                              )}`}
                              color="info"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={`Daño Oculto RE: ${formatCostaRicanCurrency(
                                calculated.hiddenDamagePartsWithVAT || 0,
                              )}`}
                              color="warning"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={`Total Repuestos: ${formatCostaRicanCurrency(
                                calculated.totalPartsLoss || 0,
                              )}`}
                              color="success"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Mano de Obra */}
                <Grid item xs={12} lg={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Mano de Obra
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Field name="bodyworkHours">
                            {({ field }: any) => (
                              <Box>
                                <TextField
                                  {...field}
                                  label="Horas Carrocería"
                                  type="number"
                                  fullWidth
                                  disabled={editMode?.active}
                                  error={
                                    touched.bodyworkHours &&
                                    Boolean(errors.bodyworkHours)
                                  }
                                  helperText={
                                    touched.bodyworkHours &&
                                    errors.bodyworkHours
                                  }
                                  inputProps={{ min: 0, step: 0.1 }}
                                  onBlur={handleFieldBlurWithAutosave(
                                    field,
                                    values,
                                  )}
                                />
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Chip
                                    label={`HCAR UT = ${convertHoursToUT(
                                      values.bodyworkHours || 0,
                                    )}`}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="paintHours">
                            {({ field }: any) => (
                              <Box>
                                <TextField
                                  {...field}
                                  label="Horas Pintura"
                                  type="number"
                                  fullWidth
                                  disabled={editMode?.active}
                                  error={
                                    touched.paintHours &&
                                    Boolean(errors.paintHours)
                                  }
                                  helperText={
                                    touched.paintHours && errors.paintHours
                                  }
                                  inputProps={{ min: 0, step: 0.1 }}
                                  onBlur={handleFieldBlurWithAutosave(
                                    field,
                                    values,
                                  )}
                                />
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Chip
                                    label={`HPIN UT = ${convertHoursToUT(
                                      values.paintHours || 0,
                                    )}`}
                                    color="secondary"
                                    variant="outlined"
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={4}>
                          <Field name="mechanicalHours">
                            {({ field }: any) => (
                              <Box>
                                <TextField
                                  {...field}
                                  label="Horas Mecánica"
                                  type="number"
                                  fullWidth
                                  disabled={editMode?.active}
                                  error={
                                    touched.mechanicalHours &&
                                    Boolean(errors.mechanicalHours)
                                  }
                                  helperText={
                                    touched.mechanicalHours &&
                                    errors.mechanicalHours
                                  }
                                  inputProps={{ min: 0, step: 0.1 }}
                                  onBlur={handleFieldBlurWithAutosave(
                                    field,
                                    values,
                                  )}
                                />
                                <Box
                                  sx={{
                                    mt: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <Chip
                                    label={`HMEC UT = ${convertHoursToUT(
                                      values.mechanicalHours || 0,
                                    )}`}
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                  />
                                </Box>
                              </Box>
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={6}>
                          <Field name="hiddenDamageLaborNoVAT">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Daño Oculto MO Sin IVA (₡)"
                                type="number"
                                fullWidth
                                error={
                                  touched.hiddenDamageLaborNoVAT &&
                                  Boolean(errors.hiddenDamageLaborNoVAT)
                                }
                                helperText={
                                  touched.hiddenDamageLaborNoVAT &&
                                  errors.hiddenDamageLaborNoVAT
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={6} lg={6}>
                          <Field name="additionalLabor">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Adicionales MO (₡)"
                                type="number"
                                fullWidth
                                disabled={editMode?.active}
                                error={
                                  touched.additionalLabor &&
                                  Boolean(errors.additionalLabor)
                                }
                                helperText={
                                  touched.additionalLabor &&
                                  errors.additionalLabor
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>

                      {/* Resultados Mano de Obra */}
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Cálculos de Mano de Obra:
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Chip
                              label={`Preliminar MO: ${formatCostaRicanCurrency(
                                calculated.preliminaryLabor || 0,
                              )}`}
                              color="info"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Chip
                              label={`Daño Oculto MO: ${formatCostaRicanCurrency(
                                calculated.hiddenDamageLaborWithVAT || 0,
                              )}`}
                              color="warning"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Chip
                              label={`Total MO: ${formatCostaRicanCurrency(
                                calculated.totalLaborLoss || 0,
                              )}`}
                              color="success"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Adicionales */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Adicionales
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Field name="additionalParts">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Adicionales RE (₡)"
                                type="number"
                                fullWidth
                                disabled={editMode?.active}
                                error={
                                  touched.additionalParts &&
                                  Boolean(errors.additionalParts)
                                }
                                helperText={
                                  touched.additionalParts &&
                                  errors.additionalParts
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                        <Grid item xs={6}>
                          <Field name="subletAmount">
                            {({ field }: any) => (
                              <TextField
                                {...field}
                                label="Monto Sublet (₡)"
                                type="number"
                                fullWidth
                                disabled={editMode?.active}
                                error={
                                  touched.subletAmount &&
                                  Boolean(errors.subletAmount)
                                }
                                helperText={
                                  touched.subletAmount && errors.subletAmount
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                onBlur={handleFieldBlurWithAutosave(
                                  field,
                                  values,
                                )}
                              />
                            )}
                          </Field>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Resumen Final - Desglose Detallado de Indemnización */}
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Resumen Final - Desglose de Indemnización
                      </Typography>

                      {/* Desglose de Costos Base */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="secondary">
                          📊 Desglose de Costos Base
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Repuestos</strong>
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Neto:{' '}
                                {formatCostaRicanCurrency(values.netParts || 0)}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Descuento ({configValues.discount}%):{' '}
                                {formatCostaRicanCurrency(
                                  (values.netParts || 0) *
                                    (configValues.discount / 100),
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1, fontWeight: 'bold' }}
                              >
                                Subtotal Repuestos (sin IVA):{' '}
                                {formatCostaRicanCurrency(
                                  calculated.netTotalParts || 0,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 'bold' }}
                              >
                                Subtotal Repuestos (con IVA {configValues.vat}
                                %):{' '}
                                {formatCostaRicanCurrency(
                                  calculated.preliminaryPartsLoss || 0,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Daño Oculto RE:{' '}
                                {formatCostaRicanCurrency(
                                  calculated.hiddenDamagePartsWithVAT || 0,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                &nbsp;
                              </Typography>
                              <Typography variant="h6" color="primary">
                                <strong>
                                  Total Repuestos:{' '}
                                  {formatCostaRicanCurrency(
                                    calculated.totalPartsLoss || 0,
                                  )}
                                </strong>
                              </Typography>
                            </Card>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Mano de Obra</strong>
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Carrocería: {values.bodyworkHours || 0}h ×{' '}
                                {formatCostaRicanCurrency(
                                  configValues.bodyworkRate,
                                )}{' '}
                                ={' '}
                                {formatCostaRicanCurrency(
                                  (values.bodyworkHours || 0) *
                                    configValues.bodyworkRate,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Pintura: {values.paintHours || 0}h ×{' '}
                                {formatCostaRicanCurrency(
                                  configValues.paintRate,
                                )}{' '}
                                ={' '}
                                {formatCostaRicanCurrency(
                                  (values.paintHours || 0) *
                                    configValues.paintRate,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Mecánica: {values.mechanicalHours || 0}h ×{' '}
                                {formatCostaRicanCurrency(
                                  configValues.mechanicalRate,
                                )}{' '}
                                ={' '}
                                {formatCostaRicanCurrency(
                                  (values.mechanicalHours || 0) *
                                    configValues.mechanicalRate,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 1, fontWeight: 'bold' }}
                              >
                                Subtotal MO (sin IVA):{' '}
                                {formatCostaRicanCurrency(
                                  calculated.preliminaryLaborNoVAT || 0,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 'bold' }}
                              >
                                Subtotal MO (con IVA {configValues.vat}%):{' '}
                                {formatCostaRicanCurrency(
                                  calculated.preliminaryLabor || 0,
                                )}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Daño Oculto MO:{' '}
                                {formatCostaRicanCurrency(
                                  calculated.hiddenDamageLaborWithVAT || 0,
                                )}
                              </Typography>
                              <Typography variant="h6" color="primary">
                                <strong>
                                  Total MO:{' '}
                                  {formatCostaRicanCurrency(
                                    calculated.totalLaborLoss || 0,
                                  )}
                                </strong>
                              </Typography>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Desglose de Adicionales y Sublet */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="secondary">
                          🔧 Adicionales y Subcontratación
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Adicionales MO</strong>
                              </Typography>
                              <Typography variant="h6" color="info">
                                {formatCostaRicanCurrency(
                                  values.additionalLabor || 0,
                                )}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Adicionales RE</strong>
                              </Typography>
                              <Typography variant="h6" color="info">
                                {formatCostaRicanCurrency(
                                  values.additionalParts || 0,
                                )}
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Card variant="outlined" sx={{ p: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>Sublet</strong>
                              </Typography>
                              <Typography variant="h6" color="warning">
                                {formatCostaRicanCurrency(
                                  values.subletAmount || 0,
                                )}
                              </Typography>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Desglose de UT Audatex */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="secondary">
                          ⚙️ Conversión UT Audatex
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Card
                              variant="outlined"
                              sx={{ p: 2, textAlign: 'center' }}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>HCAR UT</strong>
                              </Typography>
                              <Typography variant="h6" color="primary">
                                {convertHoursToUT(values.bodyworkHours || 0)} UT
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ({values.bodyworkHours || 0}h × 10)
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Card
                              variant="outlined"
                              sx={{ p: 2, textAlign: 'center' }}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>HPIN UT</strong>
                              </Typography>
                              <Typography variant="h6" color="secondary">
                                {convertHoursToUT(values.paintHours || 0)} UT
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ({values.paintHours || 0}h × 10)
                              </Typography>
                            </Card>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Card
                              variant="outlined"
                              sx={{ p: 2, textAlign: 'center' }}
                            >
                              <Typography variant="subtitle2" gutterBottom>
                                <strong>HMEC UT</strong>
                              </Typography>
                              <Typography variant="h6" color="success">
                                {convertHoursToUT(values.mechanicalHours || 0)}{' '}
                                UT
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                ({values.mechanicalHours || 0}h × 10)
                              </Typography>
                            </Card>
                          </Grid>
                        </Grid>
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Typography variant="h6" color="primary">
                            <strong>
                              Total UT:{' '}
                              {convertHoursToUT(values.bodyworkHours || 0) +
                                convertHoursToUT(values.paintHours || 0) +
                                convertHoursToUT(
                                  values.mechanicalHours || 0,
                                )}{' '}
                              UT
                            </strong>
                          </Typography>
                        </Box>
                      </Box>

                      {/* Cálculo Final de Indemnización */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="secondary">
                          💰 Cálculo Final de Indemnización
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Card variant="outlined" sx={{ p: 3 }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" gutterBottom>
                                    <strong>Monto Original de Pérdida</strong>
                                  </Typography>
                                  <Typography variant="h5" color="info">
                                    {formatCostaRicanCurrency(
                                      calculated.totalLossAmount || 0,
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Repuestos + MO + Adicionales + Sublet
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" gutterBottom>
                                    <strong>Deducible</strong>
                                  </Typography>
                                  <Typography variant="h5" color="warning">
                                    -
                                    {formatCostaRicanCurrency(
                                      configValues.deductible || 0,
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Monto deducible aplicado
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" gutterBottom>
                                    <strong>Infraseguro</strong>
                                  </Typography>
                                  <Typography variant="h5" color="error">
                                    -
                                    {formatCostaRicanCurrency(
                                      configValues.underinsurance || 0,
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Monto de infraseguro
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle1" gutterBottom>
                                    <strong>Depreciaciones</strong>
                                  </Typography>
                                  <Typography variant="h5" color="error">
                                    -
                                    {formatCostaRicanCurrency(
                                      configValues.depreciation || 0,
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Depreciación del vehículo
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Resultado Final */}
                      <Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <Card>
                              <CardContent>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    p: 2,
                                    backgroundColor: 'success.main',
                                    color: 'white',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    Total Ajuste
                                  </Typography>
                                  <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {formatCostaRicanCurrency(
                                      calculated.finalIndemnityAmount || 0,
                                    )}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Validación de Umbral para Pérdida Financiera */}
                      {configValues.isFinancialLoss &&
                        configValues.underinsurance > 0 && (
                          <Box sx={{ mt: 3 }}>
                            {(() => {
                              const indemnityPercentage =
                                (calculated.finalIndemnityAmount /
                                  configValues.underinsurance) *
                                100;
                              const threshold = 75;
                              const isOverThreshold =
                                indemnityPercentage >= threshold;

                              return (
                                <Alert
                                  severity={isOverThreshold ? 'error' : 'info'}
                                  sx={{ mb: 2 }}
                                >
                                  <Typography variant="h6" gutterBottom>
                                    <strong>
                                      Validación de Pérdida Financiera
                                    </strong>
                                  </Typography>
                                  <Typography variant="body1">
                                    <strong>
                                      Porcentaje de Indemnización:
                                    </strong>{' '}
                                    {indemnityPercentage.toFixed(2)}%
                                  </Typography>
                                  <Typography variant="body1">
                                    <strong>Umbral Aplicable:</strong>{' '}
                                    {threshold}% del infraseguro
                                  </Typography>
                                  <Typography variant="body1">
                                    <strong>Infraseguro:</strong>{' '}
                                    {formatCostaRicanCurrency(
                                      configValues.underinsurance,
                                    )}
                                  </Typography>
                                  <Typography variant="body1">
                                    <strong>Monto a Indemnizar:</strong>{' '}
                                    {formatCostaRicanCurrency(
                                      calculated.finalIndemnityAmount,
                                    )}
                                  </Typography>
                                  <Typography variant="body1">
                                    <strong>75% del Infraseguro:</strong>{' '}
                                    {formatCostaRicanCurrency(
                                      configValues.underinsurance * 0.75,
                                    )}
                                  </Typography>

                                  {isOverThreshold ? (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'error.light',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        color="white"
                                        gutterBottom
                                      >
                                        ⚠️ RECOMENDACIÓN: CERRAR CASO
                                      </Typography>
                                      <Typography variant="body1" color="white">
                                        El monto a indemnizar supera o es igual
                                        al {threshold}% del infraseguro. Se
                                        recomienda cerrar el caso como pérdida
                                        total.
                                      </Typography>
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        mt: 2,
                                        p: 2,
                                        bgcolor: 'success.light',
                                        borderRadius: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        color="white"
                                        gutterBottom
                                      >
                                        ✅ CASO CONTINUAR
                                      </Typography>
                                      <Typography variant="body1" color="white">
                                        El monto a indemnizar está dentro del
                                        umbral permitido. El caso puede
                                        continuar con reparaciones.
                                      </Typography>
                                    </Box>
                                  )}
                                </Alert>
                              );
                            })()}
                          </Box>
                        )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Desglose de Cálculos según Fórmulas - Acordeón */}
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary
                      expandIcon={
                        <span
                          style={{ fontSize: '1.5rem', fontWeight: 'bold' }}
                        >
                          ▼
                        </span>
                      }
                      aria-controls="formulas-breakdown-content"
                      id="formulas-breakdown-header"
                    >
                      <Typography variant="h6" color="primary">
                        Desglose de Cálculos según Fórmulas
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>
                              1. Monto preliminar de pérdida (Repuestos):
                            </strong>{' '}
                            Neto * Descuento = Neto Total; Neto Total * IVA =
                            Total
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Neto:{' '}
                            {formatCostaRicanCurrency(values.netParts || 0)} ×
                            (1 - {configValues.discount}%) ={' '}
                            {formatCostaRicanCurrency(
                              calculated.netTotalParts || 0,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Neto Total × (1 + {configValues.vat}%) ={' '}
                            {formatCostaRicanCurrency(
                              calculated.preliminaryPartsLoss || 0,
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>
                              2. Monto total de pérdida (Repuestos):
                            </strong>{' '}
                            Monto preliminar + Daño Oculto RE
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCostaRicanCurrency(
                              calculated.preliminaryPartsLoss || 0,
                            )}{' '}
                            +{' '}
                            {formatCostaRicanCurrency(
                              calculated.hiddenDamagePartsWithVAT || 0,
                            )}{' '}
                            ={' '}
                            {formatCostaRicanCurrency(
                              calculated.totalPartsLoss || 0,
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>
                              3. Monto preliminar de pérdida (MO):
                            </strong>{' '}
                            Horas × Tarifa (por tipo) × (1 + IVA/100)
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 2 }}
                          >
                            <strong>Conversión UT Audatex:</strong> 1 hora = 10
                            UT
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>HCAR UT:</strong>{' '}
                            {values.bodyworkHours || 0}h × 10 ={' '}
                            {convertHoursToUT(values.bodyworkHours || 0)} UT
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>HPIN UT:</strong> {values.paintHours || 0}h
                            × 10 = {convertHoursToUT(values.paintHours || 0)} UT
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>HMEC UT:</strong>{' '}
                            {values.mechanicalHours || 0}h × 10 ={' '}
                            {convertHoursToUT(values.mechanicalHours || 0)} UT
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            <strong>Total UT:</strong>{' '}
                            {convertHoursToUT(values.bodyworkHours || 0) +
                              convertHoursToUT(values.paintHours || 0) +
                              convertHoursToUT(
                                values.mechanicalHours || 0,
                              )}{' '}
                            UT
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>
                              3.1. Cálculo de Mano de Obra por Horas:
                            </strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Carrocería: {values.bodyworkHours || 0}h ×{' '}
                            {formatCostaRicanCurrency(
                              configValues.bodyworkRate,
                            )}{' '}
                            ={' '}
                            {formatCostaRicanCurrency(
                              (values.bodyworkHours || 0) *
                                configValues.bodyworkRate,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pintura: {values.paintHours || 0}h ×{' '}
                            {formatCostaRicanCurrency(configValues.paintRate)} ={' '}
                            {formatCostaRicanCurrency(
                              (values.paintHours || 0) * configValues.paintRate,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Mecánica: {values.mechanicalHours || 0}h ×{' '}
                            {formatCostaRicanCurrency(
                              configValues.mechanicalRate,
                            )}{' '}
                            ={' '}
                            {formatCostaRicanCurrency(
                              (values.mechanicalHours || 0) *
                                configValues.mechanicalRate,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>
                              Total MO (sin IVA):{' '}
                              {formatCostaRicanCurrency(
                                calculated.preliminaryLaborNoVAT || 0,
                              )}
                            </strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>
                              Total MO (con IVA):{' '}
                              {formatCostaRicanCurrency(
                                calculated.preliminaryLabor || 0,
                              )}{' '}
                              ({calculated.preliminaryLaborNoVAT || 0} × (1 +{' '}
                              {configValues.vat}%))
                            </strong>
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>4. Monto total de pérdida (MO):</strong>{' '}
                            Monto preliminar + Daño Oculto MO
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCostaRicanCurrency(
                              calculated.preliminaryLabor || 0,
                            )}{' '}
                            +{' '}
                            {formatCostaRicanCurrency(
                              calculated.hiddenDamageLaborWithVAT || 0,
                            )}{' '}
                            ={' '}
                            {formatCostaRicanCurrency(
                              calculated.totalLaborLoss || 0,
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>5. Monto total de pérdida:</strong> Total
                            Repuestos + Total MO + Adicionales (MO + RE) +
                            Sublet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCostaRicanCurrency(
                              calculated.totalPartsLoss || 0,
                            )}{' '}
                            +
                            {formatCostaRicanCurrency(
                              calculated.totalLaborLoss || 0,
                            )}{' '}
                            +
                            {formatCostaRicanCurrency(
                              values.additionalLabor || 0,
                            )}{' '}
                            +{' '}
                            {formatCostaRicanCurrency(
                              values.additionalParts || 0,
                            )}{' '}
                            +{' '}
                            {formatCostaRicanCurrency(values.subletAmount || 0)}{' '}
                            ={' '}
                            {formatCostaRicanCurrency(
                              calculated.totalLossAmount || 0,
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>6. Monto total de la reparación:</strong>{' '}
                            Equivalente a monto total de pérdida
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCostaRicanCurrency(
                              calculated.totalRepairAmount || 0,
                            )}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography variant="subtitle1" gutterBottom>
                            <strong>7. Monto a Indemnizar Inicial:</strong>{' '}
                            Monto total de la reparación - (Infraseguro +
                            Depreciaciones)
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatCostaRicanCurrency(
                              calculated.totalRepairAmount || 0,
                            )}{' '}
                            - (
                            {formatCostaRicanCurrency(
                              configValues.underinsurance,
                            )}{' '}
                            +{' '}
                            {formatCostaRicanCurrency(
                              configValues.depreciation,
                            )}
                            ) ={' '}
                            {formatCostaRicanCurrency(
                              calculated.initialIndemnityAmount || 0,
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                </Grid>

                {/* Botones de Calcular */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mt: 3,
                    }}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      size="large"
                      sx={{ minWidth: 200 }}
                      onClick={() => setOpenConfirmSaveEstimation(true)}
                    >
                      {editMode?.active
                        ? 'Actualizar Estimación'
                        : 'Guardar Cálculo de Estimación'}
                    </Button>
                    {editMode?.active && onCancelEdit && (
                      <Button
                        variant="outlined"
                        color="secondary"
                        size="large"
                        sx={{ minWidth: 200, ml: 2 }}
                        onClick={onCancelEdit}
                      >
                        Cancelar Edición
                      </Button>
                    )}
                  </Box>
                </Grid>

                {canViewBalancePanel() && (
                  <>
                    {/* Panel Balance */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          {!isValidEstimationId() && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                              Se debe cargar desde el histórico de estimaciones
                              o crear una estimación antes de proceder a agregar
                              una factura.
                            </Alert>
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              mb: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              gutterBottom
                              color="primary"
                            >
                              Balance (Lista de Facturas)
                            </Typography>
                            <Button
                              variant="contained"
                              startIcon={<Add />}
                              onClick={handleAddBalance}
                              size="small"
                              disabled={!isValidEstimationId()}
                            >
                              Agregar Balance
                            </Button>
                          </Box>

                          <Box sx={{ height: 400, width: '100%' }}>
                            <DataGrid
                              rows={balanceData.filter(
                                (item) => !item.isDeleted,
                              )}
                              columns={balanceColumns}
                              getRowId={(row) =>
                                row._id ||
                                row.balanceId ||
                                row.id ||
                                `row-${Math.random()}`
                              }
                              initialState={{
                                pagination: {
                                  paginationModel: { page: 0, pageSize: 5 },
                                },
                              }}
                              pageSizeOptions={[5, 10, 20]}
                              disableRowSelectionOnClick
                              disableColumnMenu={false}
                              disableColumnFilter={false}
                              disableColumnSelector={false}
                              processRowUpdate={(newRow) => {
                                setBalanceData(
                                  balanceData.map((row) => {
                                    const rowId =
                                      row._id || row.balanceId || row.id;
                                    const newRowId =
                                      newRow._id ||
                                      newRow.balanceId ||
                                      newRow.id;
                                    return rowId === newRowId ? newRow : row;
                                  }),
                                );
                                return newRow;
                              }}
                              sx={{
                                '& .MuiDataGrid-cell': {
                                  fontSize: '0.875rem',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                  fontSize: '0.875rem',
                                  fontWeight: 'bold',
                                },
                                '& .MuiDataGrid-columnHeader--resizing': {
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                },
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Panel de Resumen de Saldo Balance */}
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              p: 2,
                              backgroundColor: 'primary.main',
                              color: 'white',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 'bold' }}
                            >
                              Saldo Balance
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: 'bold' }}
                            >
                              {formatCostaRicanCurrency(
                                calculateBalanceAmount(
                                  calculated.totalLossAmount || 0,
                                  configValues.vat || 0,
                                ),
                              )}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              backgroundColor: 'grey.50',
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.75rem' }}
                            >
                              <strong>Fórmula:</strong> Monto Total de Pérdida -
                              Total Balance
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '0.75rem', mt: 0.5 }}
                            >
                              <strong>Desglose:</strong>{' '}
                              {formatCostaRicanCurrency(
                                calculated.totalLossAmount /
                                  (1 + configValues.vat / 100) -
                                  configValues.deductible || 0,
                              )}{' '}
                              - {formatCostaRicanCurrency(balanceTotalAmount())}
                              ={' '}
                              {formatCostaRicanCurrency(
                                calculateBalanceAmount(
                                  calculated.totalLossAmount || 0,
                                  configValues.vat || 0,
                                ),
                              )}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </>
                )}
              </Grid>
              {/* Modal de Confirmación de Guardar Estimación */}
              <Dialog open={openConfirmSaveEstimation}>
                <DialogTitle id="confirm-save-estimation-title">
                  {editMode?.active
                    ? 'Confirmar Actualización'
                    : 'Confirmar Guardar Estimación'}
                </DialogTitle>
                <DialogContent>
                  <DialogContentText
                    id="confirm-save-estimation-modal"
                    style={{ width: '550px', height: '100px' }}
                  >
                    {editMode?.active ? (
                      <p>
                        ¿Está seguro que desea <b>actualizar la estimación</b>{' '}
                        con los nuevos valores de daño oculto?
                      </p>
                    ) : (
                      <>
                        <p>
                          ¿Está seguro que desea <b>guardar la estimación</b>?
                        </p>
                        <p>
                          <b>Nota:</b> Tenga en cuenta que si guarda una nueva
                          estimación, se creará una nuevo registro en el
                          historial de estimaciones.
                        </p>
                      </>
                    )}
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setOpenConfirmSaveEstimation(false)}
                  >
                    Cancelar
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      editMode?.active
                        ? handleSubmitSave(values)
                        : handleSubmitSaveNewEstimation(values)
                    }
                  >
                    {editMode?.active ? 'Actualizar' : 'Guardar estimación'}
                  </Button>
                </DialogActions>
              </Dialog>
            </Form>
          );
        }}
      </Formik>

      {/* Modal de Balance - Fuera del Formik para evitar interferencia con autoguardado */}
      {canViewBalancePanel() && (
        <BalanceModal
          openModal={openModal}
          editingItem={editingItem}
          onClose={handleCloseModal}
          onSave={handleSaveBalance}
          generateRandomId={generateRandomId}
          configValues={configValues}
          siseSiniestro={siseSiniestro}
        />
      )}
    </Box>
  );
};

// Componente Modal separado para evitar interferencia con autoguardado
const BalanceModal: React.FC<{
  openModal: boolean;
  editingItem: any;
  onClose: () => void;
  onSave: (formData: any) => void;
  generateRandomId: () => string;
  configValues: ConfigValues;
  siseSiniestro?: string;
}> = ({
  openModal,
  editingItem,
  onClose,
  onSave,
  generateRandomId,
  configValues,
  siseSiniestro,
}) => {
  const [formData, setFormData] = React.useState({
    balanceId: generateRandomId(),
    documentType: '',
    documentNumber: '',
    date: new Date().toISOString().split('T')[0],
    recipient: '',
    amount: 0,
    sparePartsNC: 0,
    observation: '',
  });

  // Estado local para el valor visual del input de monto
  const [amountInputValue, setAmountInputValue] = React.useState('');

  React.useEffect(() => {
    if (editingItem) {
      setFormData({
        balanceId: editingItem.balanceId || '',
        documentType: editingItem.documentType || '',
        documentNumber: editingItem.documentNumber || '',
        date: editingItem.date || new Date().toISOString().split('T')[0],
        recipient: editingItem.recipient || '',
        amount: editingItem.amount || 0,
        sparePartsNC: editingItem.sparePartsNC || 0,
        observation: editingItem.observation || '',
      });
      setAmountInputValue(
        editingItem.amount ? editingItem.amount.toString() : '',
      );
    } else {
      setFormData({
        balanceId: generateRandomId(),
        documentType: '',
        documentNumber: '',
        date: new Date().toISOString().split('T')[0],
        recipient: '',
        amount: 0,
        sparePartsNC: 0,
        observation: '',
      });
      setAmountInputValue('');
    }
  }, [editingItem, openModal, generateRandomId]);

  // Calcular NC Repuesto automáticamente cuando cambie el monto (solo para FREXP)
  React.useEffect(() => {
    if (
      formData.amount > 0 &&
      configValues.discount > 0 &&
      formData.documentType === 'FREXP'
    ) {
      const sparePartsNC = formData.amount * (configValues.discount / 100);
      setFormData((prev) => ({
        ...prev,
        sparePartsNC: Math.round(sparePartsNC * 100) / 100,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        sparePartsNC: 0,
      }));
    }
  }, [formData.amount, configValues.discount, formData.documentType]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleBalanceSubmit = () => {
    // Validaciones básicas
    if (!formData.documentType) {
      alert('El Tipo de Documento es requerido');
      return;
    }
    if (!formData.date) {
      alert('La Fecha es requerida');
      return;
    }
    // Solo CDEDXP debe tener restricción de monto positivo (se convierte a negativo automáticamente)
    if (formData.documentType === 'CDEDXP' && formData.amount < 0) {
      alert(
        'Para Cobro Deducible (x Pagar), el monto debe ser mayor o igual a 0 (se aplicará como negativo automáticamente)',
      );
      return;
    }
    if (formData.sparePartsNC < 0) {
      alert('El NC Repuesto debe ser mayor o igual a 0');
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={openModal} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {editingItem
          ? 'Editar Registro de Balance'
          : 'Agregar Registro de Balance'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Balance ID */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Balance ID"
              value={formData.balanceId}
              disabled
              fullWidth
            />
          </Grid>
          {/* # Siniestro - Deshabilitado */}
          <Grid item xs={12} md={6}>
            <TextField
              label="# Siniestro"
              value={
                editingItem
                  ? editingItem.claimNumber
                  : siseSiniestro || generateRandomId()
              }
              disabled
              fullWidth
              helperText={
                siseSiniestro
                  ? 'Valor obtenido del siniestro actual'
                  : 'ID generado automáticamente'
              }
            />
          </Grid>

          {/* Tipo de Documento - Select */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Tipo de Documento</InputLabel>
              <Select
                value={formData.documentType}
                onChange={(e) =>
                  handleInputChange('documentType', e.target.value)
                }
                label="Tipo de Documento"
              >
                <MenuItem value="FMOXP">Factura MO (x Pagar)</MenuItem>
                <MenuItem value="FREXP">Factura RE (x Pagar)</MenuItem>
                <MenuItem value="CDEDXP">Cobro Deducible (x Pagar)</MenuItem>
                <MenuItem value="ADJGAS">Ajuste (Gasto)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* # de Documento */}
          <Grid item xs={12} md={6}>
            <TextField
              label="# de Documento"
              value={formData.documentNumber}
              onChange={(e) =>
                handleInputChange('documentNumber', e.target.value)
              }
              fullWidth
            />
          </Grid>

          {/* Fecha */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Fecha (dd/MM/yyyy)"
              type="date"
              value={formData.date || ''}
              onChange={(e) => {
                handleInputChange('date', e.target.value);
              }}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Receptor */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Receptor"
              value={formData.recipient}
              onChange={(e) => handleInputChange('recipient', e.target.value)}
              fullWidth
            />
          </Grid>

          {/* Monto */}
          <Grid item xs={12} md={6}>
            <TextField
              label={
                formData.documentType === 'CDEDXP'
                  ? 'Monto a Descontar (₡)'
                  : 'Monto (₡)'
              }
              type="text"
              value={amountInputValue}
              onChange={(e) => {
                const value = e.target.value;
                // Permitir números positivos y negativos, incluyendo decimales
                // Permitir empezar con "-", números, decimales, y combinaciones válidas
                if (
                  value === '' ||
                  value === '-' ||
                  value === '.' ||
                  value === '-.' ||
                  /^-?\d*\.?\d*$/.test(value)
                ) {
                  // Actualizar el valor visual del input
                  setAmountInputValue(value);

                  // Solo convertir a número si es un valor válido completo
                  let numValue = 0;
                  if (
                    value !== '' &&
                    value !== '-' &&
                    value !== '.' &&
                    value !== '-.'
                  ) {
                    const parsed = parseFloat(value);
                    if (!isNaN(parsed)) {
                      numValue = parsed;
                    }
                  }
                  handleInputChange('amount', numValue);
                }
              }}
              fullWidth
              required
              inputProps={{
                step: 0.01,
              }}
              helperText={
                formData.amount !== 0
                  ? formData.documentType === 'CDEDXP'
                    ? `Se descontará: ${formatCostaRicanCurrency(
                        -Math.abs(formData.amount),
                      )} (siempre negativo)`
                    : formData.amount > 0
                    ? `Se sumará: ${formatCostaRicanCurrency(formData.amount)}`
                    : `Se restará: ${formatCostaRicanCurrency(formData.amount)}`
                  : formData.documentType === 'CDEDXP'
                  ? 'Ingrese el monto a descontar (se aplicará como negativo)'
                  : 'Ingrese el monto (positivo suma, negativo resta)'
              }
            />
          </Grid>

          {/* NC Repuesto */}
          <Grid item xs={12} md={6}>
            <TextField
              label="NC Repuesto (₡)"
              type="number"
              value={formData.sparePartsNC}
              fullWidth
              disabled
              inputProps={{ min: 0, step: 0.01 }}
              helperText={
                formData.documentType === 'FREXP' &&
                formData.amount > 0 &&
                configValues.discount > 0
                  ? `Calculado automáticamente: ${formatCostaRicanCurrency(
                      formData.sparePartsNC,
                    )} (${configValues.discount}% de ${formatCostaRicanCurrency(
                      formData.amount,
                    )})`
                  : formData.documentType === 'FREXP'
                  ? 'Se calculará automáticamente al ingresar el monto'
                  : 'Solo aplica para Factura RE (x Pagar)'
              }
            />
          </Grid>

          {/* Observación */}
          <Grid item xs={12}>
            <TextField
              label="Observación"
              multiline
              rows={3}
              value={formData.observation}
              onChange={(e) => handleInputChange('observation', e.target.value)}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleBalanceSubmit} variant="contained">
          {editingItem ? 'Actualizar' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculationForm;
